/**
 * ?use_ios_ui (2026-10-19) — the iOS 0.7.0 selection ring, twin of
 * ARService+WheelSelectionRing: four short arcs at the wheel's 12/3/6/9
 * o'clock, inner edge ON the rim rather than a halo outside the wheel, in
 * the light blue iOS reads against bodywork (a mid blue vanishes on a blue
 * car). Built in the wheel group's own frame, so the arcs stay pinned to the
 * wheel as it turns and scale with it.
 *
 * Carries the shipped ring's name, so the renderer's selection, render-order
 * and disposal paths treat it exactly like the dashed ring it replaces.
 */

import * as THREE from 'three'
import { SELECTION_RING_NAME } from '@/ar/selection-ring'
import { SELECTION_RING_RENDER_ORDER } from '@/ar/wheel-render-order'

/** #5EC2FF — iOS selectionRingColor. */
export const IOS_SELECTION_COLOR_HEX = 0x5ec2ff

/** Arc length, degrees (iOS selectionArcDegrees). */
export const IOS_SELECTION_ARC_DEG = 33.75

const ARC_COUNT = 4
/** Radial width as a share of the rim radius — twice iOS's 0.018
 *  (dev, 2026-10-19: "2x thicker"). */
const WIDTH_FRACTION = 0.036
/** Forward of the face as a share of the rim radius (iOS selectionRingForwardFraction). */
const FORWARD_FRACTION = 0.01
const SEGMENTS_PER_ARC = 10

export function buildIosSelectionArcs(faceRadiusM: number): THREE.Group {
  if (!(faceRadiusM > 0) || !Number.isFinite(faceRadiusM)) {
    throw new Error(`buildIosSelectionArcs: face radius must be positive, got ${faceRadiusM}`)
  }
  const inner = faceRadiusM
  const outer = faceRadiusM + faceRadiusM * WIDTH_FRACTION
  const span = (IOS_SELECTION_ARC_DEG * Math.PI) / 180

  const group = new THREE.Group()
  group.name = SELECTION_RING_NAME
  group.renderOrder = SELECTION_RING_RENDER_ORDER
  group.position.z = faceRadiusM * FORWARD_FRACTION

  for (let quarter = 0; quarter < ARC_COUNT; quarter += 1) {
    const centre = (quarter * Math.PI * 2) / ARC_COUNT
    const geometry = new THREE.RingGeometry(inner, outer, SEGMENTS_PER_ARC, 1, centre - span / 2, span)
    const material = new THREE.MeshBasicMaterial({
      color: new THREE.Color(IOS_SELECTION_COLOR_HEX),
      side: THREE.DoubleSide,
      // Transparent at full opacity: three draws every opaque object before
      // every transparent one, so an opaque arc drew BEFORE the transparent
      // MASK band and was painted over (2026-10-19). In the band's queue its
      // overlay render order puts it last.
      transparent: true,
      opacity: 1,
      // UI drawn in world space: with depth on, the rim it sits on would hide it.
      depthTest: false,
      depthWrite: false,
    })
    const arc = new THREE.Mesh(geometry, material)
    arc.renderOrder = SELECTION_RING_RENDER_ORDER
    group.add(arc)
  }
  return group
}
